import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useFormServiceMatch } from "src/hooks/useFormServiceMatch";
import { useOrganization } from "src/hooks/useOrganization";
import { formatIsoDateToOrg } from "src/services/format";
import * as Url from "src/services/url";
import { Organization } from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { useRequireBulkTargets } from "./useRequireBulkTargets";

const MAX_NAME_LENGTH = 120;

type RunMatchFormServiceDialogProps = {
  selectionSize: number;
  isOpen: boolean;
  onCancel: () => void;
  matchName: string;
  onMatchNameChange: (value: string) => void;
};

export const RunMatchFormServiceDialog = ({
  selectionSize,
  isOpen,
  onCancel,
  matchName,
  onMatchNameChange,
}: RunMatchFormServiceDialogProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const organizationRD = useOrganization();
  const hasReachedLimit = matchName.length > MAX_NAME_LENGTH;
  const isError = hasReachedLimit;
  const { selectedEnrollmentPeriod } = useEnrollmentPeriod();
  const [isStarting, setIsStarting] = useState(false);
  const toast = useAvelaToast();
  const requireTargets = useRequireBulkTargets();

  const [organization, setOrganization] = React.useState<Organization>();
  const { startMatch } = useFormServiceMatch();

  React.useEffect(() => {
    if (organizationRD.kind === RD.RemoteDataKind.Success)
      setOrganization(organizationRD.data);
  }, [organizationRD]);

  const handleSubmit = React.useCallback(async () => {
    if (!organization || !selectedEnrollmentPeriod?.id) return;
    try {
      setIsStarting(true);

      const targets = await requireTargets();
      if (!targets) {
        setIsStarting(false);
        return;
      }

      const timestamp = formatIsoDateToOrg(
        new Date().toISOString(),
        organizationRD,
        "MM/dd/yyyy h:mm:ssaaa"
      );
      const tagLabel = `Match ${matchName} ${timestamp}`;
      const matchRun = await startMatch({
        matchName,
        tagLabel,
        enrollmentPeriodId: selectedEnrollmentPeriod.id,
        targets,
      });

      navigate(Url.OrgAdmin.Match.progress(organization, matchRun.matchRunId));
    } catch (error) {
      console.error("Failed to start match:", error);
      toast({
        title: "Failed to start match",
        description: "There was an error starting the match. Please try again.",
        status: "error",
      });
      setIsStarting(false);
    }
  }, [
    organization,
    selectedEnrollmentPeriod?.id,
    organizationRD,
    startMatch,
    matchName,
    navigate,
    toast,
    requireTargets,
  ]);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg">Run a match</AlertDialogHeader>
          <AlertDialogBody>
            <VStack spacing={4} align="stretch">
              <Text>
                A match will be run on {selectionSize} entries using the match
                configuration for this enrollment period.
              </Text>
              <FormControl isInvalid={isError}>
                <FormLabel>Match name</FormLabel>
                <Input
                  placeholder="Match name"
                  value={matchName}
                  onChange={(e) => onMatchNameChange(e.target.value)}
                  isDisabled={isStarting}
                />
                {!hasReachedLimit && (
                  <FormHelperText>
                    {MAX_NAME_LENGTH - matchName.length}
                  </FormHelperText>
                )}
                {hasReachedLimit && (
                  <FormErrorMessage>
                    Limit reached -{matchName.length - MAX_NAME_LENGTH}
                  </FormErrorMessage>
                )}
              </FormControl>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              variant="ghost"
              colorScheme="gray"
              ref={cancelRef}
              onClick={onCancel}
              isDisabled={isStarting}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              ml={3}
              isDisabled={!matchName || hasReachedLimit || isStarting}
              isLoading={isStarting}
            >
              Run match
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
